import {useState, useEffect, useRef, useCallback, useMemo} from "react";
import {flushSync} from "react-dom";
import {
  ShieldCheck,
  LogOut,
  LayoutDashboard,
  User,
  ChevronDown,
  Menu,
  X,
  Search,
  MonitorUp,
  FolderOpen,
  Mail,
} from "lucide-react";
import {v4 as uuidv4} from "uuid";
import {parsePhoneNumber, AsYouType} from "libphonenumber-js";
import {useLocation, useNavigate} from "react-router-dom";
import OrbitLoader from "./ui/components/OrbitLoader";

import VideoDashboard from "./features/video/pages/VideoDashboard";
import VideoUpload from "./features/video/pages/VideoUpload";
import AdminDashboard from "./features/userStats/pages/AdminDashboard";
import Account from "./features/userStats/pages/AccountDashboard";
import Settings from "./features/userSettings/pages/Settings";
import MyCaseDashboard from "./features/myCase/pages/MyCaseDashboard";
import ContactPage from "./features/contact/pages/ContactPage";
import {
  normalizeEmail,
  buildCustomerIdPath,
  isProfileComplete,
} from "./utils/customerAuth";

import {COUNTRIES} from "./config";
import useAPIService from "./hooks/useAPIService";
import {
  startSignIn,
  confirmOtp,
  signOut,
  isAdmin,
  getIdToken,
} from "./features/auth/cognitoAuth";
import LoginView from "./features/auth/components/LoginView";
import LoginFormView from "./features/auth/components/LoginFormView";
import SignupCompleteView from "./features/auth/components/SignupCompleteView";
import LoginVerifyView from "./features/auth/components/LoginVerifyView";
import {
  sessionBackend,
  setRememberDevice,
} from "./features/auth/rememberDevice";
import {applyTokenStorage} from "./features/auth/amplifyConfig";
import {rankRecentlyWatched} from "./features/video/utils/recentlyWatched";

const initialFormState = {
  firstname: "",
  lastname: "",
  avatar: "",
  phone: "",
  email: "",
  confirmationCode: "",
  country_dialcode: COUNTRIES[0].dialCode,
  country_code: COUNTRIES[0].code,
  country_name: COUNTRIES[0].countryName,
};

const getYouTubeThumbnail = (url) => {
  if (!url) return "./images/default_thumbnail.png";
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  const videoId = match && match[2].length === 11 ? match[2] : null;
  return videoId
    ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
    : "./images/default_thumbnail.png";
};

// Single source of truth for the resend-OTP cooldown (previously a bare `60`
// duplicated at each call site; Khubaib bug #2). The window escalates per
// send within one verify session: Twilio delivers ~97% of codes in seconds,
// so a user whose text is stuck (rare carrier delay, 1-5 min observed) can
// retry after 15s instead of being parked for a full minute -- while repeat
// taps back off, since every resend invalidates the previous code.
const RESEND_COOLDOWN_STEPS_SECONDS = [15, 30, 60];

// Hide the "My Work Plan" tab for the July 9 soft launch. The participant
// portal (work-plan PDF + real CRM data) is a fast-follow, and the tab
// currently renders mock data, so it must not ship to real participants.
// Flip to true to re-enable once the portal is ready.
const SHOW_WORK_PLAN = false;

// Permalinks: each in-app tab has a real, shareable path. currentView is
// derived from the URL and navigation writes the URL, so the browser Back
// button, refresh, bookmarks, and SMS deep-links all work. "/" resolves to
// the dashboard. Unknown paths also fall back to the dashboard (see
// currentView below), and unauthenticated access is bounced to /login.
const VIEW_TO_PATH = {
  dashboard: "/dashboard",
  mycase: "/mycase",
  videos: "/videos",
  admin: "/admin",
  account: "/account",
  contact: "/contact",
  settings: "/settings",
};
const PATH_TO_VIEW = {
  "/": "dashboard",
  "/dashboard": "dashboard",
  "/mycase": "mycase",
  "/videos": "videos",
  "/admin": "admin",
  "/account": "account",
  "/contact": "contact",
  "/settings": "settings",
};

// --- Main App ---
const App = () => {
  //Loading States
  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const [formData, setFormData] = useState(initialFormState);
  const [allVideos, setAllVideos] = useState([]);

  // Recently Watched is DERIVED, never stored: it had two writers (a
  // login-snapshot seed and a live recompute effect) and the stored list
  // drifted whenever they raced (QA 07-29/30: entries stale or vanishing).
  // One source of truth: the dashboard's live watchHistory once it has
  // reported in, else the login snapshot; ranked at render time.
  const [loginWatchHistory, setLoginWatchHistory] = useState(null);
  const [liveWatchHistory, setLiveWatchHistory] = useState(null);

  // Routing: the active tab is derived from the URL path rather than held in
  // state, so every page is a real permalink. setCurrentView navigates, which
  // updates the URL and (via PATH_TO_VIEW) the rendered view. Unknown paths
  // fall back to the dashboard.
  const location = useLocation();
  const navigate = useNavigate();
  const currentView = PATH_TO_VIEW[location.pathname] || "dashboard";
  const setCurrentView = useCallback(
    (view) => navigate(VIEW_TO_PATH[view] || "/dashboard"),
    [navigate],
  );

  // The mount-time session restore is async (it verifies the Cognito token
  // behind a stored session). Until it resolves we don't know whether the
  // user is signed in, so the auth guard must wait — otherwise a valid
  // session refreshing on /account would be bounced to /login before the
  // token check finishes.
  const [authResolved, setAuthResolved] = useState(false);
  // Where an unauthenticated visitor was headed, captured by the guard before
  // it redirects to /login and replayed once they sign in. This is what makes
  // an SMS deep-link (e.g. /dashboard?v=abc) survive the login detour.
  const redirectTargetRef = useRef(null);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  // Sends so far in the current verify session; indexes the cooldown ladder.
  const sendCountRef = useRef(0);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  // Search States
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  const profileRef = useRef(null);
  const sidebarRef = useRef(null);
  const searchInputRef = useRef(null);
  const mainRef = useRef(null);
  const {apiFetch} = useAPIService();

  // <main> is one persistent scroll container shared by every tab, so its
  // scrollTop survives view switches. Coming back to the dashboard from a
  // scrolled-down Account/Contact page left main stuck partway down, hiding
  // the dashboard header copy -- and the video grid's own overscroll-contain
  // scroller can't chain a swipe up to main, so users couldn't recover.
  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [currentView]);

  const [loginState, setLoginState] = useState({
    state: "login",
    message: null,
    message_type: null,
    user: null,
  });

  const [authMethod, setAuthMethod] = useState("phone");
  // "Keep me signed in on this device". Default ON (opt-out): this population
  // is overwhelmingly on personal phones, so most users get the 30-day cache
  // without noticing a checkbox; unchecking falls back to sessionStorage
  // (cleared on browser close) for shared machines.
  const [rememberDevice, setRememberDeviceState] = useState(true);

  const fetchRecentVideos = useCallback(
    async (user) => {
      const {result: videoResult, data: videoData} = await apiFetch(
        `/get-videos`,
        {method: "GET"},
      );

      if (videoResult === "success") {
        setAllVideos(videoData);
      }

      // Keep only the raw login snapshot; ranking happens in the
      // derivation below so there is exactly one code path producing
      // the sidebar list.
      setLoginWatchHistory(user.watch_history || {});
    },
    [apiFetch],
  );

  // The dashboard reports every live watchHistory change (merged with the
  // server by its fetch hooks); once it has, the snapshot is obsolete.
  const handleWatchHistoryChange = useCallback((nextHistory) => {
    setLiveWatchHistory(nextHistory);
  }, []);

  const recentVideos = useMemo(
    () => rankRecentlyWatched(liveWatchHistory ?? loginWatchHistory, allVideos),
    [liveWatchHistory, loginWatchHistory, allVideos],
  );

  /* Helper Functions */
  const getE164Phone = (phone = formData.phone) => {
    const raw = `${formData.country_dialcode}${phone}`;
    try {
      const parsed = parsePhoneNumber(raw, formData.country_code);
      return parsed?.isValid() ? parsed.format("E.164") : raw;
    } catch {
      return raw;
    }
  };

  // Helpers delegate to pure functions in ./utils/customerAuth so the
  // login flow's correctness can be exercised by unit tests without
  // rendering App.
  const getAuthCustomerId = () =>
    authMethod === "email" ? normalizeEmail(formData.email) : getE164Phone();

  // Hide the Admin/Upload tabs from non-admins, from the Cognito `admin`
  // group claim. UI-only; real enforcement is server-side (requireAdmin).
  const [showAdmin, setShowAdmin] = useState(false);
  // Tracks whether the async admin check has finished, so the admin-route
  // guard below doesn't bounce a real admin during the check.
  const [adminChecked, setAdminChecked] = useState(false);
  useEffect(() => {
    if (!loginState.user) {
      setShowAdmin(false);
      setAdminChecked(false);
      return;
    }
    let cancelled = false;
    isAdmin()
      .then((admin) => {
        if (!cancelled) setShowAdmin(admin);
      })
      .catch(() => {
        if (!cancelled) setShowAdmin(false);
      })
      .finally(() => {
        if (!cancelled) setAdminChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [loginState.user]);

  // Routes are now directly reachable by URL, so a route whose content is
  // gated off would render a blank page under its header. Redirect those to
  // the dashboard:
  //  - /mycase while the Work Plan tab is hidden pre-launch (SHOW_WORK_PLAN),
  //  - /admin and /videos for non-admins, once the admin check has resolved
  //    (waiting on adminChecked avoids bouncing a real admin mid-check).
  // Server-side requireAdmin remains the real enforcement; this is UX.
  useEffect(() => {
    if (loginState.state !== "logged_in") return;
    if (currentView === "mycase" && !SHOW_WORK_PLAN) {
      navigate("/dashboard", {replace: true});
      return;
    }
    if (
      (currentView === "admin" || currentView === "videos") &&
      adminChecked &&
      !showAdmin
    ) {
      navigate("/dashboard", {replace: true});
    }
  }, [loginState.state, adminChecked, showAdmin, currentView, navigate]);

  /* Dashboard Handlers */
  // YouTube-logo-style home reset: the header title and the sidebar
  // "Job Training" tile both return to the plain dashboard, clearing any
  // active search (and any open ?v=/?playlist= state, since /dashboard is
  // navigated without params) so the browse rows come back.
  const goHome = () => {
    setSearchQuery("");
    setIsMobileSearchOpen(false);
    setIsSidebarOpen(false);
    navigate("/dashboard");
  };

  const handleSearch = (query) => setSearchQuery(query);

  /* Authentication & Profile Handlers */
  const handleInputChange = (e) => {
    const {id, value} = e.target;
    setFormData((prev) => ({...prev, [id]: value}));
  };

  const handleCompleteProfile = async () => {
    setIsActionLoading(true);
    const customerId = getAuthCustomerId();
    try {
      // No existence pre-check: a brand-new Cognito account has no contact
      // row yet -- this PATCH is the upsert that creates it.
      const ts = new Date().toISOString();
      await apiFetch("/update-contact/name", {
        method: "PATCH",
        body: JSON.stringify({
          customer_id_u: customerId,
          customer_firstname_u: formData.firstname,
          customer_lastname_u: formData.lastname,
          timestamp_u: ts,
        }),
      });
      const user = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        customer_id: customerId,
        avatar: formData.avatar,
        avatarUrl: formData.avatarUrl,
      };
      setLoginState({state: "logged_in", user, message: null});
      sessionBackend().setItem("userSession", JSON.stringify(user));
    } catch {
      setLoginState((p) => ({
        ...p,
        message: "Unable to process profile update.",
        message_type: "error",
      }));
    } finally {
      setIsActionLoading(false);
    }
  };

  // One passwordless flow for both new and returning users. Cognito custom-auth
  // sends the code and auto-creates the account on first sign-in (open
  // self-signup), so we never need to know -- or tell the user -- whether this
  // is a "sign in" or a "sign up". After verification, handleVerifyOTP routes
  // purely by profile completeness.
  // rawOverride: bulk entry (autofill/paste) continues with the value straight
  // from the input event -- state may not have re-rendered yet when it fires.
  const handleContinue = async (rawOverride) => {
    setIsActionLoading(true);
    const customerId =
      typeof rawOverride === "string"
        ? authMethod === "email"
          ? normalizeEmail(rawOverride)
          : getE164Phone(rawOverride)
        : getAuthCustomerId();

    // A send from the entry screen (first attempt, or back-and-retry) starts
    // a fresh cooldown ladder; sends from the verify screen are resends and
    // climb it.
    if (loginState.state !== "otp_verify") sendCountRef.current = 0;

    // Lock in the storage choice BEFORE the challenge starts, so the tokens it
    // issues (and the session blob) land in the store the user picked. Doing it
    // here rather than at verify time avoids switching Amplify's backend
    // mid-challenge, which can drop the in-flight auth state.
    setRememberDevice(rememberDevice);
    applyTokenStorage(rememberDevice);

    try {
      // Cognito custom-auth: this sends the OTP (and auto-creates the account
      // on first sign-in). Replaces the old /update-contact/otp send.
      await startSignIn(customerId);
      // A fresh code was just sent, so any previously typed code is dead --
      // showing it back (e.g. after Back -> Continue, or Resend) invites
      // verifying a stale code.
      setFormData((p) => ({...p, confirmationCode: ""}));
      setLoginState({
        state: "otp_verify",
        message: `Verification code sent to ${customerId}.`,
        message_type: "success",
        user: null,
      });
      const step = Math.min(
        sendCountRef.current,
        RESEND_COOLDOWN_STEPS_SECONDS.length - 1,
      );
      setResendTimer(RESEND_COOLDOWN_STEPS_SECONDS[step]);
      sendCountRef.current += 1;
      // On resend, digits from the now-dead code must not linger in the boxes.
      resetOtpInput();
    } catch (err) {
      // A thrown error means no code was sent, so stay on the entry screen.
      // Cognito throttles repeated attempts; show a wait message for that and
      // a generic one otherwise.
      const throttled =
        err?.name === "LimitExceededException" ||
        err?.name === "TooManyRequestsException";
      setLoginState((p) => ({
        ...p,
        message: throttled
          ? "Too many attempts. Please wait a moment and try again."
          : "Error sending code.",
        message_type: "error",
      }));
    } finally {
      setIsActionLoading(false);
    }
  };

  // A failed or superseded code leaves stale digits in the boxes; clear them
  // and put the cursor back on the first box so retyping starts immediately.
  // (No-op focus on the entry screen, where the boxes aren't mounted yet.)
  const resetOtpInput = () => {
    setFormData((p) => ({...p, confirmationCode: ""}));
    document.getElementById("otp-0")?.focus();
  };

  // codeOverride: bulk entry (autofill/WebOTP/paste) verifies the delivered
  // code directly -- state may not have re-rendered yet when it fires.
  const handleVerifyOTP = async (codeOverride) => {
    setIsActionLoading(true);
    const customerId = getAuthCustomerId();

    // Verify the code first, in its own try: a failure here is an auth
    // problem (wrong/expired code). Failures AFTER this point are data-API
    // problems on a signed-in user and must not be reported as
    // "verification failed", which misleads the user into re-requesting
    // codes (and each retry signs the good session back out).
    let signedIn = false;
    try {
      // Cognito verifies the code server-side and issues a signed token.
      // No more reading the OTP from the API or comparing it in the browser.
      signedIn = await confirmOtp(
        typeof codeOverride === "string"
          ? codeOverride
          : formData.confirmationCode,
      );
    } catch {
      resetOtpInput();
      setLoginState((p) => ({
        ...p,
        message:
          "That code didn't work. Use the newest code we sent you, or tap Resend Code to get a new one.",
        message_type: "error",
      }));
      setIsActionLoading(false);
      return;
    }
    if (!signedIn) {
      resetOtpInput();
      setLoginState((p) => ({
        ...p,
        message: "Incorrect code.",
        message_type: "error",
      }));
      setIsActionLoading(false);
      return;
    }

    try {
      // Verified. The browser now holds a Cognito token (sent automatically on
      // every API call). Load the profile row to route by completeness. A
      // brand-new account has no contact row yet -> profile incomplete ->
      // complete_signup, which creates the row via /update-contact/name.
      const {data} = await apiFetch(
        `/get-contact/id/${buildCustomerIdPath(customerId)}`,
      );
      const dbUser = data?.[0] || {};
      const user = {
        firstname: dbUser.customer_firstname,
        lastname: dbUser.customer_lastname,
        customer_id: customerId,
        avatar: dbUser.avatar,
        watch_history: dbUser.watch_history,
      };

      if (dbUser.avatar) {
        const {imageURL} = await apiFetch(
          `/get-contact/avatar/${dbUser.avatar}`,
        );
        if (imageURL) user.avatarUrl = imageURL;
      }

      // Route by profile completeness alone: a complete profile lands on the
      // dashboard; a row with no name goes to profile completion.
      const profileComplete = isProfileComplete(dbUser);

      if (profileComplete) {
        setFormData((prev) => ({
          ...prev,
          firstname: dbUser.customer_firstname || "",
          lastname: dbUser.customer_lastname || "",
          phone:
            authMethod === "phone"
              ? dbUser.customer_id?.replace("+1", "") || ""
              : prev.phone,
          email: authMethod === "email" ? customerId : prev.email,
          avatar: dbUser.avatar || "",
          avatarUrl: user.avatarUrl || "",
          confirmationCode: "",
        }));
        setLoginState({state: "logged_in", user, message: null});
        sessionBackend().setItem("userSession", JSON.stringify(user));
      } else {
        setLoginState({state: "complete_signup", user, message: null});
      }
    } catch {
      // The user IS signed in (code verified above); only the profile load
      // failed. Say so, and do not blame the code they typed.
      setLoginState((p) => ({
        ...p,
        message: "Signed in, but we couldn't load your profile. Try again.",
        message_type: "error",
      }));
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    setIsProfileOpen(false);
    try {
      // Cognito owns the session now: clear its tokens. Best-effort — local
      // sign-out below runs regardless so the user is never stuck signed in.
      await signOut();
    } catch (e) {
      console.error(e);
    } finally {
      setFormData(initialFormState);
      setLoginState({state: "login", message: null, user: null});
      // Clear the session from whichever store held it, and forget the device:
      // an explicit sign-out should not silently keep a remembered session.
      // The checkbox resets to the default (checked) for the next login; the
      // persisted flag + storage only re-engage when that login starts.
      window.sessionStorage.removeItem("userSession");
      window.localStorage.removeItem("userSession");
      setRememberDevice(false);
      setRememberDeviceState(true);
      applyTokenStorage(false);
      // Signing out is a fresh start: drop any remembered deep-link so the
      // next login lands on the dashboard, and go to the login screen.
      redirectTargetRef.current = null;
      navigate("/login", {replace: true});
      setIsLoading(false);
    }
  };

  const handleOnBack = async () => {
    // Always return to the single entry screen. We deliberately do NOT delete
    // the contact here: in the unified flow we can't distinguish a brand-new
    // ghost row from a returning user's real account, and deleting the latter
    // would destroy their account. A nameless ghost is harmless -- the next
    // code request simply resumes it.
    setFormData((p) => ({...p, confirmationCode: ""}));
    setLoginState((p) => ({
      ...p,
      state: "login",
      message: null,
      message_type: null,
    }));
  };

  const handlePhoneChange = (e) => {
    const countryCode = formData.country_code || "US";
    // Always derive formatting from clean digits rather than feeding
    // the previously-formatted string back through AsYouType. The old
    // pattern produced inconsistent intermediate formats and visible
    // jitter as the parser re-evaluated punctuation on every keystroke
    // (Khubaib bug #9 / EL-4). Stripping to digits first means each
    // format pass is deterministic on raw input and only grows/shrinks
    // with the actual digit count. We cap at 15 -- the E.164 maximum.
    const rawNext = e.target.value || "";
    let digits = rawNext.replace(/\D/g, "").slice(0, 15);

    // People often type their number with the country code ("1 208..." or
    // "+1 208..."). A US area code can never start with 1, so an 11th digit
    // behind a leading 1 is unambiguously the country code -- drop it and
    // keep the 10-digit national number.
    if (digits.length > 10 && digits.startsWith("1")) {
      digits = digits.slice(1);
    }

    // Round-3b: AsYouType formats e.g. "(332) " so a single backspace
    // from "(332) 4" eats the digit but a backspace from "(332) " only
    // eats the trailing space, leaves digits === "332", and AsYouType
    // re-adds "(332) " on the next render. To the user it looks like
    // the first three digits are uneraseable. Detect that case --
    // input shrank but the digit count is identical -- and drop the
    // last digit by hand.
    const prevFormatted = formData.phone || "";
    const prevDigits = prevFormatted.replace(/\D/g, "");
    if (
      rawNext.length < prevFormatted.length &&
      digits.length === prevDigits.length &&
      digits.length > 0
    ) {
      digits = digits.slice(0, -1);
    }

    const formatted = new AsYouType(countryCode).input(digits);
    setFormData((prev) => ({...prev, phone: formatted}));
  };

  /* Profile Update & Deletion Handlers */
  const handleUpdateProfile = async (name) => {
    setIsActionLoading(true);
    try {
      await apiFetch("/update-contact/name", {
        method: "PATCH",
        body: JSON.stringify({
          customer_id_u: loginState.user?.customer_id,
          customer_firstname_u: name.firstname,
          customer_lastname_u: name.lastname,
          timestamp_u: new Date().toISOString(),
        }),
      });
      setFormData((prev) => ({
        ...prev,
        firstname: name.firstname,
        lastname: name.lastname,
      }));
      setLoginState((prev) => ({
        ...prev,
        user: {
          ...prev.user,
          firstname: name.firstname,
          lastname: name.lastname,
        },
      }));
    } finally {
      setIsActionLoading(false);
    }
  };

  useEffect(() => {
    // Read the session from whichever store holds it (localStorage when the
    // user opted to stay signed in, else sessionStorage). The binding itself
    // stays per-tab (sessionStorage + window.name); a remembered session
    // restored into a fresh tab gets bound on restore below, so the hijack
    // check covers it from the next navigation onward.
    const userSession = sessionBackend().getItem("userSession");
    const sessionBoundId = sessionStorage.getItem("tab_bound_id");
    // `window.name` is preserved across same-tab navigations but is wiped
    // by browsers in several legitimate cases: cross-origin redirects,
    // tabs opened via `target="_blank" rel="noopener"`, and some hard
    // reloads. Treating an empty `window.name` as a security violation
    // means a real user gets booted with "Re-login for this tab" on a
    // perfectly normal page open. Only clear the session when `window.name`
    // is populated AND disagrees with the bound id -- that's the actual
    // session-hijack case (someone reused this tab handle for another
    // session). When `window.name` is empty, re-seed it from sessionStorage.
    if (
      userSession &&
      sessionBoundId &&
      window.name &&
      window.name !== sessionBoundId
    ) {
      sessionStorage.clear();
      // Defense in depth: a remembered session blob lives in localStorage, so
      // clearing sessionStorage alone would leave it behind on this hijack path.
      window.localStorage.removeItem("userSession");
      setRememberDevice(false);
      // Revoke the Cognito tokens BEFORE repointing storage, else the
      // remembered copies stay valid in localStorage for up to 30 days
      // after the very event that told us the session is compromised.
      (async () => {
        try {
          await signOut();
        } catch (e) {
          console.error(e);
        }
        applyTokenStorage(false);
      })();
      setLoginState((p) => ({
        ...p,
        state: "login",
        message: "Security: Re-login for this tab.",
        message_type: "error",
      }));
      setAuthResolved(true);
    } else if (userSession) {
      if (sessionBoundId && !window.name) {
        window.name = sessionBoundId;
      } else if (!sessionBoundId) {
        // A remembered session restored into a fresh tab (localStorage blob,
        // but this tab was never bound; sessionStorage is per-tab). Bind it
        // now so the hijack check above is active for remembered sessions
        // too, instead of only for same-tab sessionStorage ones.
        const id = uuidv4();
        window.name = id;
        sessionStorage.setItem("tab_bound_id", id);
      }
      // A corrupt/truncated session blob (interrupted write, storage
      // eviction, an older shape) must never escape this effect: an
      // uncaught throw here leaves authResolved false forever, so the auth
      // guard never arms and the deep-link/URL-cleanup effects stop --
      // a dead white screen with no way out. Drop the bad blob and resolve
      // to a fresh login, the same fallback as a session with no token.
      let parsedUser = null;
      try {
        parsedUser = JSON.parse(userSession);
      } catch {
        sessionBackend().removeItem("userSession");
        setAuthResolved(true);
      }
      const isEmailUser = parsedUser?.customer_id?.includes("@");
      // A stored session is only as good as the Cognito token behind it.
      // Sessions persisted before the JWT cutover (or whose token is gone)
      // used to restore into a "logged in" shell where every API call
      // 401s with no recovery path. Verify the token BEFORE trusting the
      // stored blob, then route by profile completeness -- same rule as the
      // post-OTP path (isProfileComplete) -- so a refresh mid-signup
      // resumes at the name form instead of skipping it (ghost accounts).
      (async () => {
        if (!parsedUser) return; // corrupt blob, already resolved to login
        let hasToken = false;
        try {
          hasToken = Boolean(await getIdToken());
        } catch {
          /* no Cognito session */
        }
        if (!hasToken) {
          sessionBackend().removeItem("userSession");
          setAuthResolved(true);
          return; // stay on the login screen; a fresh OTP sign-in is required
        }
        if (isEmailUser) setAuthMethod("email");
        setFormData((prev) => ({
          ...prev,
          firstname: parsedUser.firstname || "",
          lastname: parsedUser.lastname || "",
          phone: isEmailUser
            ? ""
            : parsedUser.customer_id?.replace("+1", "") || "",
          email: isEmailUser ? parsedUser.customer_id : "",
          avatar: parsedUser.avatar || "",
          avatarUrl: parsedUser.avatarUrl || "",
        }));
        const hasName =
          (parsedUser.firstname || "").trim() ||
          (parsedUser.lastname || "").trim();
        if (!hasName) {
          setLoginState({
            state: "complete_signup",
            user: parsedUser,
            message: null,
          });
          setAuthResolved(true);
          return;
        }
        setLoginState({state: "logged_in", user: parsedUser, message: null});
        setAuthResolved(true);
        // The avatar URL persisted in sessionStorage is a presigned S3 link
        // that expires, so on reopen the navbar image would eventually 404.
        // Re-resolve it from the stored avatar key (the same key the Admin
        // page uses, which is why the avatar shows there but not in the
        // navbar) so the navbar avatar stays consistent across sessions.
        if (parsedUser.avatar) {
          apiFetch(
            `/get-contact/avatar/${buildCustomerIdPath(parsedUser.avatar)}`,
            {
              method: "GET",
            },
          )
            .then(({imageURL}) => {
              if (!imageURL) return;
              setFormData((prev) => ({...prev, avatarUrl: imageURL}));
              setLoginState((prev) => ({
                ...prev,
                user: {...prev.user, avatarUrl: imageURL},
              }));
            })
            .catch(() => {});
        }
        apiFetch(`/get-videos`, {method: "GET"}).then((res) => {
          if (res.result === "success") setAllVideos(res.data);
        });
      })();
    } else {
      const id = uuidv4();
      window.name = id;
      sessionStorage.setItem("tab_bound_id", id);
      setAuthResolved(true);
    }

    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target))
        setIsProfileOpen(false);
      if (
        isSidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target)
      )
        setIsSidebarOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside, {passive: true});
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSidebarOpen, apiFetch]);

  // Auth guard: an unauthenticated visitor cannot reach any page. Once the
  // session restore has resolved, anyone not logged in on a non-login path is
  // bounced to /login, and where they were headed is remembered so the login
  // flow can drop them back there (this is what carries an SMS deep-link
  // through the login detour). Waiting on authResolved avoids booting a valid
  // session mid-restore.
  useEffect(() => {
    if (!authResolved) return;
    if (loginState.state === "logged_in") return;
    if (location.pathname === "/login") return;
    redirectTargetRef.current = location.pathname + location.search;
    navigate("/login", {replace: true});
  }, [
    authResolved,
    loginState.state,
    location.pathname,
    location.search,
    navigate,
  ]);

  // Post-login: replay the remembered destination (or default to the
  // dashboard) as soon as the user is signed in, so a deep-link resumes and a
  // plain login lands somewhere sensible instead of sitting on /login.
  useEffect(() => {
    if (loginState.state !== "logged_in") return;
    const target = redirectTargetRef.current;
    redirectTargetRef.current = null;
    if (target && target !== "/login") {
      navigate(target, {replace: true});
    } else if (location.pathname === "/login") {
      navigate("/dashboard", {replace: true});
    }
  }, [loginState.state, location.pathname, navigate]);

  // Deep-link params (?v=<id>, ?playlist=<id>) are owned by the dashboard:
  // the URL is the source of truth for the open player/playlist there, so
  // the params persist while watching/browsing (shareable, refreshable,
  // Back-button-friendly). All this effect does is normalize a deep-link
  // that landed on some other path onto /dashboard, params intact.
  useEffect(() => {
    if (loginState.state !== "logged_in") return;
    if (location.pathname === "/dashboard") return;
    const params = new URLSearchParams(location.search);
    if (!params.has("v") && !params.has("playlist")) return;
    navigate(
      {pathname: "/dashboard", search: location.search},
      {replace: true},
    );
  }, [loginState.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setInterval(() => setResendTimer((p) => p - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [resendTimer]);

  // Auto-close the mobile sidebar whenever the active view changes.
  // Most call sites already invoke setIsSidebarOpen(false), but the
  // setCurrentView calls inside auth-flow side effects (line ~449,
  // 518, 924) do not. Adding a single effect here covers every entry
  // point uniformly so the menu never sticks open after navigation
  // (Khubaib bug #13 hardening; awaiting a precise repro before
  // claiming a full fix).
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [currentView]);

  useEffect(() => {
    console.log("Login state user changed:", loginState.user);
    if (loginState.user) {
      sessionBackend().setItem("userSession", JSON.stringify(loginState.user));
      fetchRecentVideos(loginState.user);
    }
  }, [loginState.user, fetchRecentVideos]);

  /* UI Components */
  // Plain JSX value, NOT a nested component: `const SidebarContent = () =>`
  // defined inside App creates a fresh component type on every App render,
  // which makes React unmount/remount the whole sidebar subtree each time.
  // The logo <img> remounting is visible as a flicker whenever App re-renders
  // (e.g. the every-few-seconds watch-history updates while a video plays).
  const sidebarContent = (
    <>
      <div className="p-6 mb-2 flex items-center justify-between">
        <img src="./images/ccx_logo.png" className="w-32" alt="Logo" />
        <button
          onClick={() => setIsSidebarOpen(false)}
          className="lg:hidden p-2 rounded-full bg-slate-100 text-slate-500"
        >
          <X size={20} />
        </button>
      </div>
      <nav className="px-4 space-y-1">
        <p className="text-[10px] font-bold text-slate-400 px-6 py-2 uppercase tracking-wider">
          General
        </p>
        {[
          {id: "dashboard", icon: LayoutDashboard, label: "Job Training"},
          {id: "mycase", icon: FolderOpen, label: "My Work Plan"},
          {id: "videos", icon: MonitorUp, label: "Upload Videos"},
          {id: "admin", icon: ShieldCheck, label: "Admin"},
          {id: "account", icon: User, label: "Account"},
          {id: "contact", icon: Mail, label: "Contact Us"},
        ]
          .filter((item) => SHOW_WORK_PLAN || item.id !== "mycase")
          .filter(
            (item) =>
              showAdmin || (item.id !== "admin" && item.id !== "videos"),
          )
          .map((item) => (
            <button
              key={item.id}
              onClick={() => {
                // The Job Training tile doubles as "home": also reset any
                // active search, YouTube-logo style.
                if (item.id === "dashboard") {
                  goHome();
                  return;
                }
                setCurrentView(item.id);
                setIsSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-6 py-3 rounded-full transition-all ${currentView === item.id ? "bg-[#E6EDF4] text-[#191919] font-bold" : "text-slate-500 hover:bg-slate-50"}`}
            >
              <item.icon size={20} />{" "}
              <span className="capitalize">{item.label}</span>
            </button>
          ))}
      </nav>
      <div className="mt-8 px-4 flex-1 overflow-y-auto">
        <p className="text-[10px] font-bold text-slate-400 px-6 py-2 uppercase tracking-wider">
          Recently Watched
        </p>
        <div className="space-y-2 pb-10">
          {recentVideos.map((video) => (
            <button
              type="button"
              key={video.videoId}
              // Open this video directly in the dashboard player. The player
              // is URL-driven (?v=), so this is just a navigation -- the
              // dashboard resolves the id and pops the modal.
              onClick={() => {
                navigate(
                  `/dashboard?v=${encodeURIComponent(video.videoId ?? video.id)}`,
                );
                setIsSidebarOpen(false);
              }}
              className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-white rounded-2xl cursor-pointer group transition-all focus:outline-none focus:ring-2 focus:ring-indigo-300"
            >
              <img
                src={getYouTubeThumbnail(video.url)}
                className="w-10 h-10 rounded-xl object-cover shadow-sm"
                alt={video.title}
              />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold text-slate-700 truncate group-hover:text-indigo-600">
                  {video.title}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </>
  );

  const isSignupFormValid =
    formData.firstname.trim() !== "" && formData.lastname.trim() !== "";

  // Views that size themselves to the viewport and scroll in their own
  // inner container (the video grid; Upload's lg: two-pane layout). Only
  // these get hard h-full wrappers. Every other view must flow naturally
  // in <main>: a fixed h-full wrapper pinned the footer + bottom padding
  // at viewport height, so taller pages (Account / Contact Us on mobile)
  // overflowed underneath them and the last rows ended flush against the
  // screen edge with the footer painted on top.
  const viewFillsViewport =
    currentView === "dashboard" || currentView === "videos";

  // h-dvh-with-vh-fallback (index.css) sizes the shell to the *visible*
  // viewport so the bottom of the content isn't hidden behind mobile Chrome's
  // dynamic URL bar. It must stay a single custom class: pairing two Tailwind
  // height utilities (screen + a dvh one) lets stylesheet order pick the
  // winner, and Tailwind emits the screen one last, so the shell was silently
  // 100vh and mobile Chrome clipped the bottom of every page (footer
  // half-hidden on prod). Don't name the old utilities here, even in a
  // comment: Tailwind's scanner reads comments and would re-emit them.
  return (
    <div className="flex h-dvh-with-vh-fallback bg-white antialiased overflow-hidden relative">
      {/* The drawer + backdrop must stack above the VideoModal (z-[9999],
          z-[10000] fullscreen): the mobile modal leaves the top bar visible,
          so the hamburger stays tappable while a video is open and the
          drawer would otherwise slide out hidden behind the player. */}
      {/* Always mounted, opacity-toggled: mounting it at open popped the
          backdrop blur in with no transition -- part of the drawer-open
          "shutter". pointer-events-none while hidden keeps taps working. */}
      {loginState.state === "logged_in" && (
        <div
          aria-hidden="true"
          className={`fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[10010] lg:hidden transition-opacity duration-300 ${isSidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        />
      )}
      {loginState.state === "logged_in" && (
        <aside
          ref={sidebarRef}
          // z-[10020] is for the MOBILE drawer, which must slide out above an
          // open VideoModal (z-[9999]). On lg+ the sidebar is pinned page
          // chrome, so it drops back to auto -- the desktop video overlay
          // dims and covers it like the rest of the page.
          className={`fixed inset-y-0 left-0 z-[10020] lg:z-auto w-[85%] max-w-[320px] bg-[#F8FAFC] border-r border-[#E6EDF4] flex flex-col transition-transform duration-300 will-change-transform
    lg:relative lg:translate-x-0 lg:w-72 
    ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
        >
          {sidebarContent}
        </aside>
      )}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {loginState.state === "logged_in" && (
          <header className="h-20 border-b border-[#F1F5F9] flex items-center justify-between px-6 lg:px-10 bg-white relative">
            <div
              className={`flex items-center gap-4 ${isMobileSearchOpen ? "hidden lg:flex" : "flex"}`}
            >
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="lg:hidden p-2 rounded-xl bg-slate-50 text-slate-600 border border-slate-200"
              >
                <Menu size={24} />
              </button>
              {/* The page title doubles as a home button (YouTube-logo
                  pattern): tap it to return to the plain dashboard with the
                  search cleared. */}
              <h1 className="text-xl lg:text-3xl font-bold text-[#1a1a1a] capitalize">
                <button
                  type="button"
                  onClick={goHome}
                  // hover: is gated to hover-capable devices: on touch, iOS
                  // applies :hover on tap and keeps it until the next tap or
                  // a scroll, so the title sat gray after navigating home.
                  // active: gives touch its pressed feedback and releases
                  // the moment the finger lifts.
                  className="transition-opacity active:opacity-60 [@media(hover:hover)]:hover:opacity-70"
                  aria-label="Go to Job Training home"
                >
                  {currentView === "dashboard"
                    ? "Job Training"
                    : currentView === "mycase"
                      ? "My Work Plan"
                      : currentView === "contact"
                        ? "Contact Us"
                        : currentView === "account"
                          ? "Account"
                          : currentView.charAt(0).toUpperCase() +
                            currentView.slice(1)}
                </button>
              </h1>
            </div>

            <div
              className={`flex items-center gap-2 sm:gap-4 justify-end ${isMobileSearchOpen ? "flex-1" : "lg:flex-initial"}`}
            >
              {currentView === "dashboard" && (
                <div
                  className={`relative ${isMobileSearchOpen ? "w-full" : ""}`}
                >
                  {!isMobileSearchOpen && (
                    <button
                      className="lg:hidden p-2 rounded-full bg-[#f1f5ff] text-slate-900 border border-[#e2e8f0] flex items-center justify-center transition-colors hover:bg-[#e2e8f0]"
                      aria-label="Search videos"
                      onClick={() => {
                        // flushSync so the input is visible before focus();
                        // both inside this tap, or iOS won't open the
                        // keyboard and Marco needs a second tap.
                        flushSync(() => setIsMobileSearchOpen(true));
                        searchInputRef.current?.focus();
                      }}
                    >
                      <Search size={19} strokeWidth={2} />
                    </button>
                  )}

                  {/* SEARCH INPUT: Always visible on Desktop, visible on Mobile only when expanded */}
                  <div
                    className={`${isMobileSearchOpen ? "flex" : "hidden"} lg:block relative w-full lg:max-w-xs z-[100]`}
                  >
                    <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                      <Search size={22} className="text-black" />
                    </div>
                    <input
                      ref={searchInputRef}
                      autoFocus={isMobileSearchOpen}
                      type="text"
                      placeholder="Search videos..."
                      value={searchQuery}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        // Commit: drop the keyboard and exit any open player
                        // (?v=) -- searching from the half-screen video left
                        // the results invisible underneath it. /dashboard
                        // with no params closes the player; the query lives
                        // in state so the results are showing when it does.
                        e.preventDefault();
                        e.target.blur();
                        navigate("/dashboard");
                      }}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="w-full bg-white border border-[#cbd9ff] rounded-full py-1 lg:py-1.5 pl-14 pr-12 outline-none text-lg font-[urbanist] text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-blue-400 transition-all"
                    />

                    {/* MOBILE CLOSE ICON (Inside the input) */}
                    {isMobileSearchOpen && (
                      <button
                        onClick={() => {
                          setIsMobileSearchOpen(false);
                          setSearchQuery("");
                        }}
                        className="absolute inset-y-0 right-0 pr-4 flex items-center lg:hidden"
                      >
                        <div className="p-0.5 rounded-full border border-slate-400 text-slate-600">
                          <X size={16} strokeWidth={3} />
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* PROFILE SECTION (Hidden on mobile when search is expanded) */}
              <div
                className={`relative ${isMobileSearchOpen ? "hidden lg:block" : "block"}`}
                ref={profileRef}
              >
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center gap-3 hover:bg-[#F8FAFC] p-1 lg:pr-4 rounded-full transition-colors"
                >
                  <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full overflow-hidden bg-slate-200 border-2 border-white shadow-sm">
                    {formData?.avatarUrl ? (
                      <img
                        src={formData.avatarUrl}
                        alt="User"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-indigo-100 text-indigo-600 font-bold">
                        {formData?.firstname?.charAt(0) || ""}
                        {formData?.lastname?.charAt(0) || ""}
                      </div>
                    )}
                  </div>
                  <span className="font-semibold text-slate-700 hidden lg:block">
                    {formData?.firstname} {formData?.lastname}
                  </span>
                  <ChevronDown
                    size={18}
                    className={`text-slate-400 hidden lg:block transition-transform ${isProfileOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {/* Must stack above VideoModal (z-[9999]): the mobile modal
                    starts below the top bar, so the avatar stays tappable
                    while a video is open and the menu has to paint over it. */}
                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-72 bg-white border border-slate-100 rounded-[32px] shadow-xl px-4 py-3 z-[10010]">
                    <div className="px-2 pt-1 pb-2">
                      <h3 className="text-[17px] font-bold text-gray-900 leading-tight">
                        {formData?.firstname} {formData?.lastname}
                      </h3>
                      {/* Show the sign-in identity: helps a participant
                          notice they're on the wrong account (credit is
                          keyed to the contact on file). */}
                      {(formData?.email || formData?.phone) && (
                        <p className="text-[15px] text-gray-500 mt-1 break-all">
                          Signed in as{" "}
                          <span className="text-gray-800 font-medium">
                            {formData?.email || `+1 ${formData?.phone}`}
                          </span>
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => {
                          setCurrentView("settings");
                          setIsProfileOpen(false);
                        }}
                        className="w-full px-4 py-2 bg-[#E9F1F5] text-left text-[16px] text-gray-900 rounded-2xl flex items-center gap-3 font-medium"
                      >
                        <User
                          size={22}
                          strokeWidth={1.5}
                          className="text-gray-700"
                        />{" "}
                        Account settings
                      </button>
                      <button
                        onClick={handleSignOut}
                        className="w-full px-4 py-2 text-left text-[16px] text-[#FF5A43] hover:bg-rose-50 rounded-2xl flex items-center gap-3 font-medium"
                      >
                        <LogOut size={22} strokeWidth={1.5} /> Log out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </header>
        )}
        {/* overscroll-contain here is a safety net for views that scroll
            directly in <main> (login / account / admin). The learner video
            grid actually scrolls in VideoDashboard's own inner overflow
            container, which carries its own overscroll-contain.
            We deliberately do NOT add transform/will-change GPU promotion
            here: it would make <main> the containing block for the
            fixed-position modals rendered inside it, breaking their
            full-viewport overlay. */}
        <main
          ref={mainRef}
          className={`flex-1 overflow-y-auto overscroll-contain ${
            loginState.state !== "logged_in"
              ? "flex items-center justify-center bg-white md:bg-[radial-gradient(circle_at_center,_#bbdaf3_0%,_#fdfdfe_100%)]"
              : // flex-col makes short pages behave: the view wrapper grows to
                // fill the leftover height (so loaders can center in it) and
                // the footer sits at the bottom of the screen instead of
                // floating mid-page right under short content.
                // The dashboard's VideoDashboard is already a self-padded scroll
                // container; adding <main> padding too doubled the whitespace
                // under the header (the gap that cut off "Your Activity").
                // Other logged-in views aren't self-padded, so they keep it.
                currentView === "dashboard"
                ? "flex flex-col"
                : "flex flex-col p-6 md:p-10"
          }`}
        >
          {isLoading ? (
            <div className="w-full flex-1 flex items-center justify-center">
              <OrbitLoader />
            </div>
          ) : (
            <div
              className={
                loginState.state !== "logged_in"
                  ? "bg-white w-full max-w-[600px] h-full md:h-auto md:min-h-[500px] md:rounded-[60px] p-6 md:p-12 flex flex-col items-center shadow-none md:shadow-lg transition-all relative"
                  : // shrink-0 keeps self-scrolling views at exactly viewport
                    // height now that <main> is a flex column (flex would
                    // otherwise shrink them by the footer's height). Content
                    // views instead grow (flex-1) so short pages and loading
                    // states can center vertically and push the footer to the
                    // bottom of the screen.
                    viewFillsViewport
                    ? "w-full h-full shrink-0"
                    : "w-full flex-1 flex flex-col"
              }
            >
              {loginState.state !== "logged_in" && (
                <LoginView
                  {...{
                    loginState,
                    authMethod,
                    setAuthMethod,
                  }}
                />
              )}
              {loginState.state === "login" && (
                <LoginFormView
                  {...{
                    loginState,
                    formData,
                    getE164Phone,
                    handleContinue,
                    handlePhoneChange,
                    handleInputChange,
                    authMethod,
                    isActionLoading,
                    rememberDevice,
                    setRememberDevice: setRememberDeviceState,
                  }}
                />
              )}
              {loginState.state === "otp_verify" && (
                <LoginVerifyView
                  formData={formData}
                  setFormData={setFormData}
                  loginState={loginState}
                  handleVerifyOTP={handleVerifyOTP}
                  onResend={handleContinue}
                  handleOnBack={handleOnBack}
                  isActionLoading={isActionLoading}
                  resendTimer={resendTimer}
                />
              )}
              {loginState.state === "complete_signup" && (
                <SignupCompleteView
                  {...{
                    formData,
                    isSignupFormValid,
                    isActionLoading,
                    handleInputChange,
                    handleCompleteProfile,
                    handleOnBack,
                  }}
                />
              )}
              {loginState.state === "logged_in" && (
                <div
                  className={
                    viewFillsViewport
                      ? "w-full h-full"
                      : "w-full flex-1 flex flex-col"
                  }
                >
                  {currentView === "dashboard" && (
                    <VideoDashboard
                      user={loginState.user}
                      recentVideos={recentVideos}
                      filteredQuery={searchQuery}
                      onWatchHistoryChange={handleWatchHistoryChange}
                    />
                  )}
                  {currentView === "mycase" && SHOW_WORK_PLAN && (
                    <MyCaseDashboard />
                  )}
                  {currentView === "contact" && <ContactPage />}
                  {currentView === "admin" && showAdmin && <AdminDashboard />}
                  {currentView === "account" && (
                    <Account loggedInUser={loginState.user} />
                  )}
                  {currentView === "settings" && (
                    <Settings
                      formData={formData}
                      COUNTRIES={COUNTRIES}
                      onSignOut={handleSignOut}
                      onUpdateProfile={handleUpdateProfile}
                      isActionLoading={isActionLoading}
                    />
                  )}
                  {currentView === "videos" && showAdmin && <VideoUpload />}
                </div>
              )}
            </div>
          )}
          {loginState.state === "logged_in" && (
            <footer className="mt-8 pb-[calc(1.5rem+env(safe-area-inset-bottom))] text-center text-xs text-slate-400">
              Powered by CommunityCX
            </footer>
          )}
        </main>
      </div>
    </div>
  );
};

export default App;
